/**
 * League Enrollment Component - Player enrollment management within a league
 *
 * Individual leagues enroll one player at a time. Fixed-pairs leagues enroll
 * players in locked blocks of two: the organizer selects two available players
 * and submits them as a team (shared pairId on the backend). The roster groups
 * players by team and flags any player whose partner has withdrawn as needing
 * to be re-paired before rounds can be generated.
 */

import { apiService } from "../services/api.js";
import { Player, Enrollment } from "../types/index.js";
import { showNotification } from "../utils/ui.js";
import { ConfirmationModal } from "./ConfirmationModal.js";

export class LeagueEnrollmentComponent {
  private container: HTMLElement;
  private leagueId: number;
  private isFixedPairs: boolean;
  private availablePlayers: Player[] = [];
  private enrolledPlayers: Enrollment[] = [];
  private searchQuery: string = "";
  // Skill-level (tier) range filter for the available-players list. `null`
  // means the bound is open; otherwise a 0–5 tier matched against Player.level.
  private minLevel: number | null = null;
  private maxLevel: number | null = null;
  private confirmationModal: ConfirmationModal;
  // For fixed-pairs leagues: players currently selected to form the next team
  // (max two; submitted together via enroll-pair).
  private selectedForPair: number[] = [];

  constructor(container: HTMLElement, leagueId: number, mode?: string) {
    this.container = container;
    this.leagueId = leagueId;
    this.isFixedPairs = mode === "fixed-pairs";
    this.confirmationModal = new ConfirmationModal();
  }

  async render() {
    try {
      await this.loadData();
      this.renderUI();
      this.attachEventListeners();
    } catch (error) {
      console.error("Error rendering LeagueEnrollmentComponent:", error);
      showNotification("Failed to load enrollment data", "error");
    }
  }

  private async loadData() {
    try {
      const [availableResponse, enrolledResponse] = await Promise.all([
        apiService.getAvailablePlayers(this.leagueId),
        apiService.getLeaguePlayers(this.leagueId),
      ]);

      if (availableResponse.success) {
        this.availablePlayers = availableResponse.data || [];
      }

      if (enrolledResponse.success) {
        this.enrolledPlayers = enrolledResponse.data || [];
      }
    } catch (error) {
      console.error("Error loading enrollment data:", error);
      throw error;
    }
  }

  /** Group enrolled players into complete teams and leftover unpaired players. */
  private groupRoster(): {
    teams: Array<[Enrollment, Enrollment]>;
    unpaired: Enrollment[];
  } {
    const byPair = new Map<string, Enrollment[]>();
    const unpaired: Enrollment[] = [];

    for (const e of this.enrolledPlayers) {
      if (e.pairId) {
        if (!byPair.has(e.pairId)) byPair.set(e.pairId, []);
        byPair.get(e.pairId)!.push(e);
      } else {
        unpaired.push(e);
      }
    }

    const teams: Array<[Enrollment, Enrollment]> = [];
    for (const members of byPair.values()) {
      if (members.length === 2) {
        teams.push([members[0], members[1]]);
      } else {
        // Half-pair (partner withdrawn) — treat remaining member as unpaired.
        unpaired.push(...members);
      }
    }

    return { teams, unpaired };
  }

  /**
   * Available players narrowed by the current name search and skill-level
   * filter. Pure over the cached `availablePlayers` list, so it never touches
   * selection state (`selectedForPair`).
   */
  private getFilteredPlayers(): Player[] {
    const query = this.searchQuery.toLowerCase();
    return this.availablePlayers.filter((p) => {
      const level = p.level ?? 0;
      const matchesName = `${p.name}`.toLowerCase().includes(query);
      const matchesMin = this.minLevel === null || level >= this.minLevel;
      const matchesMax = this.maxLevel === null || level <= this.maxLevel;
      return matchesName && matchesMin && matchesMax;
    });
  }

  /** Render the 0–5 <option> list for a level select, marking `selected`. */
  private renderLevelOptions(selected: number | null, prefix: string): string {
    return [0, 1, 2, 3, 4, 5]
      .map(
        (lvl) =>
          `<option value="${lvl}" ${selected === lvl ? "selected" : ""}>${prefix}: ${lvl}</option>`
      )
      .join("");
  }

  private renderUI() {
    const filteredPlayers = this.getFilteredPlayers();
    const isFiltering =
      this.searchQuery !== "" || this.minLevel !== null || this.maxLevel !== null;

    this.container.innerHTML = `
      <div class="space-y-8">
        <!-- Add Player Section -->
        <div class="bg-white dark:bg-surface-dark daylight:bg-day-card rounded-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-6 shadow-sm daylight:shadow-day-sm">
          <h3 class="text-xl font-bold text-slate-900 dark:text-white mb-2">
            ${this.isFixedPairs ? "Add a Pair to League" : "Add Players to League"}
          </h3>
          ${
            this.isFixedPairs
              ? `<p class="text-sm text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-4">Select two players to lock them together as a team for this league.</p>`
              : ""
          }

          ${this.isFixedPairs ? this.renderPairSelectionBar() : ""}

          <div class="space-y-4">
            <div class="flex flex-col sm:flex-row gap-3">
              <div class="relative flex-1">
                <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">search</span>
                <input
                  id="playerSearch"
                  type="text"
                  placeholder="Search by name..."
                  value="${this.searchQuery}"
                  class="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken text-slate-900 dark:text-white daylight:text-day-ink focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>
              <div class="flex gap-3">
                <div class="relative flex-1 sm:w-32">
                  <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">filter_list</span>
                  <select
                    id="minLevel"
                    aria-label="Minimum skill level"
                    class="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken text-slate-900 dark:text-white daylight:text-day-ink focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none cursor-pointer"
                  >
                    <option value="" ${this.minLevel === null ? "selected" : ""}>Min: any</option>
                    ${this.renderLevelOptions(this.minLevel, "Min")}
                  </select>
                </div>
                <div class="relative flex-1 sm:w-32">
                  <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">filter_list</span>
                  <select
                    id="maxLevel"
                    aria-label="Maximum skill level"
                    class="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken text-slate-900 dark:text-white daylight:text-day-ink focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none cursor-pointer"
                  >
                    <option value="" ${this.maxLevel === null ? "selected" : ""}>Max: any</option>
                    ${this.renderLevelOptions(this.maxLevel, "Max")}
                  </select>
                </div>
              </div>
            </div>
            ${
              filteredPlayers.length === 0
                ? `
              <div class="p-8 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken rounded-xl text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 border-dashed">
                <span class="material-symbols-outlined text-4xl mb-2">person_off</span>
                <p>
                ${
                  this.availablePlayers.length === 0
                    ? "All players are already enrolled in this league"
                    : isFiltering
                      ? "No players found matching this level criteria"
                      : "No players match your search"
                }
                </p>
              </div>
            `
                : `
              <div class="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                ${filteredPlayers
                  .map((player) => this.renderAvailableRow(player))
                  .join("")}
              </div>
            `
            }
          </div>
        </div>

        <!-- Current Roster Section -->
        <div class="bg-white dark:bg-surface-dark daylight:bg-day-card rounded-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-6 shadow-sm daylight:shadow-day-sm">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-bold text-slate-900 dark:text-white">Current Roster</h3>
            <span class="badge badge-primary">${this.enrolledPlayers.length} Players</span>
          </div>

          ${
            this.enrolledPlayers.length === 0
              ? `
            <div class="p-12 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken rounded-xl text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-center border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 border-dashed">
              <span class="material-symbols-outlined text-5xl mb-3 opacity-50">groups</span>
              <p class="text-lg font-medium">No players enrolled yet</p>
              <p class="text-sm mt-1">Add players from the section above to get started</p>
            </div>
          `
              : this.isFixedPairs
                ? this.renderPairsRoster()
                : this.renderIndividualRoster()
          }
        </div>
      </div>
    `;
  }

  private renderPairSelectionBar(): string {
    const names = this.selectedForPair.map(
      (id) => this.availablePlayers.find((p) => p.id === id)?.name ?? `#${id}`
    );
    const slotA = names[0] ?? "—";
    const slotB = names[1] ?? "—";
    const ready = this.selectedForPair.length === 2;

    return `
      <div class="flex items-center gap-3 mb-4 p-3 rounded-xl bg-primary/5 border border-primary/20">
        <span class="material-symbols-outlined text-primary">group_add</span>
        <div class="flex-1 text-sm text-slate-700 dark:text-slate-300">
          <span class="font-semibold">${slotA}</span>
          <span class="mx-2 text-slate-400">+</span>
          <span class="font-semibold">${slotB}</span>
        </div>
        ${
          this.selectedForPair.length > 0
            ? `<button id="clearPairBtn" class="text-xs text-slate-500 daylight:text-day-ink-muted hover:text-slate-700 dark:hover:text-slate-300 daylight:hover:text-day-ink underline">Clear</button>`
            : ""
        }
        <button
          id="enrollPairBtn"
          class="btn-primary py-1.5 px-3 text-sm h-auto min-h-0 ${ready ? "" : "opacity-50 cursor-not-allowed"}"
          ${ready ? "" : "disabled"}
        >
          <span class="material-symbols-outlined text-sm">check</span>
          <span>Enroll Pair</span>
        </button>
      </div>
    `;
  }

  private renderAvailableRow(player: Player): string {
    const selected = this.selectedForPair.includes(player.id);
    const actionBtn = this.isFixedPairs
      ? `
        <button
          class="selectPairBtn btn-primary py-1.5 px-3 text-sm h-auto min-h-0 ${selected ? "ring-2 ring-primary" : ""}"
          data-id="${player.id}"
        >
          <span class="material-symbols-outlined text-sm">${selected ? "check" : "add"}</span>
          <span>${selected ? "Selected" : "Select"}</span>
        </button>`
      : `
        <button
          class="enrollBtn btn-primary py-1.5 px-3 text-sm h-auto min-h-0"
          data-id="${player.id}"
        >
          <span class="material-symbols-outlined text-sm">add</span>
          <span>Add</span>
        </button>`;

    return `
      <div class="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken rounded-xl border ${selected ? "border-primary" : "border-slate-200 dark:border-slate-800 daylight:border-day-ink/10"} hover:border-primary/50 transition-colors group">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white daylight:text-navy-800 font-bold shadow-sm">
            ${player.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <div class="font-bold text-slate-900 dark:text-white">${player.name}</div>
            <div class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Level: ${player.level ?? 0}</div>
          </div>
        </div>
        <div class="flex items-center gap-4">${actionBtn}</div>
      </div>
    `;
  }

  private renderIndividualRoster(): string {
    return `
      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
              <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Name</th>
              <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Level</th>
              <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">ELO</th>
              <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Enrolled</th>
              <th class="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-right">Action</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-200 dark:divide-slate-800 daylight:divide-day-ink/10">
            ${this.enrolledPlayers.map((e) => this.renderRosterRow(e)).join("")}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderPairsRoster(): string {
    const { teams, unpaired } = this.groupRoster();

    const teamsHtml = teams
      .map(
        (team, idx) => `
        <div class="p-4 bg-slate-50 dark:bg-slate-800/30 daylight:bg-day-sunken rounded-xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-bold uppercase tracking-wider text-slate-400 daylight:text-day-ink-muted">Team ${idx + 1}</span>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            ${team.map((member) => this.renderPairMemberCard(member)).join("")}
          </div>
        </div>`
      )
      .join("");

    const unpairedHtml =
      unpaired.length > 0
        ? `
        <div class="p-4 rounded-xl border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 daylight:bg-amber-100/60">
          <div class="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div class="flex items-center gap-2">
              <span class="material-symbols-outlined text-amber-600 dark:text-amber-400">warning</span>
              <span class="text-sm font-bold text-amber-700 dark:text-amber-300 daylight:text-amber-800">Orphan / Unpaired Players (${unpaired.length})</span>
            </div>
            ${
              unpaired.length >= 2
                ? `<button
                    id="autoPairBtn"
                    class="btn-primary py-1.5 px-3 text-sm h-auto min-h-0"
                    title="Automatically pair all unpaired players into teams"
                  >
                    <span class="material-symbols-outlined text-sm">auto_awesome</span>
                    <span>Auto-Pair All</span>
                  </button>`
                : ""
            }
          </div>
          <p class="text-xs text-amber-700/80 dark:text-amber-300/80 daylight:text-amber-800/80 mb-3">These players lost their partner or enrolled alone. Pair each of them with another unpaired player, or with an available player (who will be enrolled), before generating rounds.${
            unpaired.length % 2 === 1 && this.availablePlayers.length === 0
              ? " With an odd number and no available players left, one will remain unpaired and will receive a tournament bye."
              : ""
          }</p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            ${unpaired.map((member) => this.renderOrphanCard(member, unpaired)).join("")}
          </div>
        </div>`
        : "";

    return `<div class="space-y-4">${teamsHtml}${unpairedHtml}</div>`;
  }

  /**
   * Render an orphan card with a "Pair With..." selector, a Pair action, and the
   * usual remove button. The selector offers two groups of candidates: other
   * existing orphans, and available (not-yet-enrolled) players who get enrolled
   * on the spot when chosen. Hidden only when there is nobody to pair with.
   */
  private renderOrphanCard(member: Enrollment, allOrphans: Enrollment[]): string {
    const others = allOrphans.filter((o) => o.playerId !== member.playerId);

    const orphanOptions = others
      .map((o) => `<option value="${o.playerId}">${o.playerName ?? "Unknown"}</option>`)
      .join("");
    const availableOptions = this.availablePlayers
      .map((p) => `<option value="${p.id}">${p.name} (new)</option>`)
      .join("");

    const optionGroups = [
      orphanOptions
        ? `<optgroup label="Unpaired players">${orphanOptions}</optgroup>`
        : "",
      availableOptions
        ? `<optgroup label="Available players (will be enrolled)">${availableOptions}</optgroup>`
        : "",
    ].join("");

    const hasCandidates = others.length > 0 || this.availablePlayers.length > 0;

    const pairControls = hasCandidates
      ? `
        <div class="flex items-center gap-2 mt-2">
          <select
            class="pairWithSelect flex-1 text-xs py-1.5 px-2 rounded-lg border border-amber-300 dark:border-amber-700 daylight:border-amber-400 bg-white dark:bg-surface-dark daylight:bg-day-card text-slate-900 dark:text-white daylight:text-day-ink focus:outline-none focus:ring-2 focus:ring-primary/50"
            data-id="${member.playerId}"
            aria-label="Pair ${member.playerName ?? "player"} with"
          >
            <option value="">Pair with…</option>
            ${optionGroups}
          </select>
          <button
            class="pairOrphanBtn btn-primary py-1.5 px-3 text-xs h-auto min-h-0"
            data-id="${member.playerId}"
          >
            <span class="material-symbols-outlined text-sm">link</span>
            <span>Pair</span>
          </button>
        </div>`
      : `<p class="text-xs text-amber-700/70 dark:text-amber-300/70 daylight:text-amber-800/70 mt-2">No other player available to pair with. Add a player first.</p>`;

    return `
      <div class="p-3 rounded-lg bg-white dark:bg-surface-dark daylight:bg-day-card border border-amber-300 dark:border-amber-700 daylight:border-amber-400">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white daylight:text-navy-800 font-bold text-sm">
              ${(member.playerName ?? "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div class="font-semibold text-slate-900 dark:text-white daylight:text-day-ink text-sm">${member.playerName ?? "Unknown"}</div>
              <div class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Level: ${member.level ?? 0}</div>
            </div>
          </div>
          <button
            class="withdrawBtn text-red-500 hover:text-red-600 dark:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
            data-id="${member.playerId}"
            title="Remove Player"
          >
            <span class="material-symbols-outlined text-base">person_remove</span>
          </button>
        </div>
        ${pairControls}
      </div>
    `;
  }

  private renderPairMemberCard(member: Enrollment, needsPartner = false): string {
    return `
      <div class="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-surface-dark daylight:bg-day-card border ${needsPartner ? "border-amber-300 dark:border-amber-700" : "border-slate-200 dark:border-slate-800 daylight:border-day-ink/10"}">
        <div class="flex items-center gap-3">
          <div class="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white daylight:text-navy-800 font-bold text-sm">
            ${(member.playerName ?? "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <div class="font-semibold text-slate-900 dark:text-white text-sm">${member.playerName ?? "Unknown"}</div>
            <div class="text-xs text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">Level: ${member.level ?? 0}</div>
          </div>
        </div>
        <button
          class="withdrawBtn text-red-500 hover:text-red-600 dark:text-red-400 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
          data-id="${member.playerId}"
          title="Remove Player"
        >
          <span class="material-symbols-outlined text-base">person_remove</span>
        </button>
      </div>
    `;
  }

  private renderRosterRow(enrollment: Enrollment): string {
    return `
      <tr class="hover:bg-slate-50 dark:hover:bg-slate-800/30 daylight:hover:bg-day-sunken transition-colors">
        <td class="px-6 py-4 font-medium text-slate-900 dark:text-white daylight:text-day-ink">${enrollment.playerName}</td>
        <td class="px-6 py-4 text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${enrollment.level ?? 0}</td>
        <td class="px-6 py-4">
          <span class="badge badge-warning text-xs">${enrollment.elo.toFixed(0)}</span>
        </td>
        <td class="px-6 py-4 text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${new Date(enrollment.enrolledAt).toLocaleDateString()}</td>
        <td class="px-6 py-4 text-right">
          <button
            class="withdrawBtn text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
            data-id="${enrollment.playerId}"
            title="Remove Player"
          >
            <span class="material-symbols-outlined">person_remove</span>
          </button>
        </td>
      </tr>
    `;
  }

  private attachEventListeners() {
    const searchInput = this.container.querySelector("#playerSearch") as HTMLInputElement;
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.searchQuery = (e.target as HTMLInputElement).value;
        this.renderUI();
        this.attachEventListeners();
        // Restore focus/caret to the search box after re-render.
        const input = this.container.querySelector("#playerSearch") as HTMLInputElement;
        if (input) {
          input.focus();
          input.setSelectionRange(input.value.length, input.value.length);
        }
      });
    }

    const minLevelSelect = this.container.querySelector("#minLevel") as HTMLSelectElement;
    if (minLevelSelect) {
      minLevelSelect.addEventListener("change", (e) => {
        const value = (e.target as HTMLSelectElement).value;
        this.minLevel = value === "" ? null : parseInt(value, 10);
        this.renderUI();
        this.attachEventListeners();
      });
    }

    const maxLevelSelect = this.container.querySelector("#maxLevel") as HTMLSelectElement;
    if (maxLevelSelect) {
      maxLevelSelect.addEventListener("change", (e) => {
        const value = (e.target as HTMLSelectElement).value;
        this.maxLevel = value === "" ? null : parseInt(value, 10);
        this.renderUI();
        this.attachEventListeners();
      });
    }

    // Individual mode: single enroll
    this.container.querySelectorAll(".enrollBtn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const playerId = parseInt((e.currentTarget as HTMLElement).getAttribute("data-id") || "0");
        this.handleEnroll(playerId);
      });
    });

    // Fixed-pairs mode: select players then enroll as a pair
    this.container.querySelectorAll(".selectPairBtn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const playerId = parseInt((e.currentTarget as HTMLElement).getAttribute("data-id") || "0");
        this.togglePairSelection(playerId);
      });
    });

    const enrollPairBtn = this.container.querySelector("#enrollPairBtn");
    if (enrollPairBtn) {
      enrollPairBtn.addEventListener("click", () => this.handleEnrollPair());
    }

    const clearPairBtn = this.container.querySelector("#clearPairBtn");
    if (clearPairBtn) {
      clearPairBtn.addEventListener("click", () => {
        this.selectedForPair = [];
        this.renderUI();
        this.attachEventListeners();
      });
    }

    // Orphan pairing: pair one orphan with the player chosen in its selector.
    this.container.querySelectorAll(".pairOrphanBtn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const playerAId = parseInt((e.currentTarget as HTMLElement).getAttribute("data-id") || "0");
        const select = this.container.querySelector(
          `.pairWithSelect[data-id="${playerAId}"]`
        ) as HTMLSelectElement | null;
        const playerBId = parseInt(select?.value || "0");
        this.handlePairOrphans(playerAId, playerBId);
      });
    });

    const autoPairBtn = this.container.querySelector("#autoPairBtn");
    if (autoPairBtn) {
      autoPairBtn.addEventListener("click", () => this.handleAutoPairOrphans());
    }

    this.container.querySelectorAll(".withdrawBtn").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        const playerId = parseInt((e.currentTarget as HTMLElement).getAttribute("data-id") || "0");
        this.handleWithdraw(playerId);
      });
    });
  }

  private async handlePairOrphans(playerAId: number, playerBId: number) {
    if (!playerBId) {
      showNotification("Choose a player to pair with first", "info");
      return;
    }
    try {
      await apiService.pairOrphans(this.leagueId, playerAId, playerBId);
      showNotification("Players paired successfully", "success");
      await this.render();
    } catch (error: any) {
      console.error("Error pairing orphans:", error);
      showNotification(error?.message || "Error pairing players", "error");
    }
  }

  private async handleAutoPairOrphans() {
    try {
      const response = await apiService.autoPairOrphans(this.leagueId);
      const created = response?.data?.pairsCreated ?? 0;
      const leftover = response?.data?.leftover ?? null;
      const message =
        created === 0
          ? "No players could be paired"
          : leftover
            ? `Created ${created} pair(s); one player remains unpaired and will get a bye`
            : `Created ${created} pair(s)`;
      showNotification(message, created === 0 ? "info" : "success");
      await this.render();
    } catch (error: any) {
      console.error("Error auto-pairing orphans:", error);
      showNotification(error?.message || "Error auto-pairing players", "error");
    }
  }

  private togglePairSelection(playerId: number) {
    const idx = this.selectedForPair.indexOf(playerId);
    if (idx >= 0) {
      this.selectedForPair.splice(idx, 1);
    } else if (this.selectedForPair.length < 2) {
      this.selectedForPair.push(playerId);
    } else {
      showNotification("A pair has only two players. Clear or enroll the current selection first.", "info");
      return;
    }
    this.renderUI();
    this.attachEventListeners();
  }

  private async handleEnroll(playerId: number) {
    try {
      const response = await apiService.enrollPlayer(this.leagueId, playerId);
      if (response.success) {
        showNotification("Player enrolled successfully", "success");
        await this.render();
      } else {
        showNotification(response.error || "Failed to enroll player", "error");
      }
    } catch (error) {
      console.error("Error enrolling player:", error);
      showNotification("Error enrolling player", "error");
    }
  }

  private async handleEnrollPair() {
    if (this.selectedForPair.length !== 2) {
      showNotification("Select exactly two players to form a pair", "info");
      return;
    }

    const [playerAId, playerBId] = this.selectedForPair;
    try {
      await apiService.enrollPair(this.leagueId, playerAId, playerBId);
      showNotification("Pair enrolled successfully", "success");
      this.selectedForPair = [];
      await this.render();
    } catch (error: any) {
      console.error("Error enrolling pair:", error);
      showNotification(error?.message || "Error enrolling pair", "error");
    }
  }

  private async handleWithdraw(playerId: number) {
    const player = this.enrolledPlayers.find((e) => e.playerId === playerId);
    if (!player) return;

    const message = this.isFixedPairs
      ? `Remove ${player.playerName}? Their partner will stay enrolled but will need to be paired with someone else.`
      : `Are you sure you want to remove ${player.playerName}?`;

    const confirmed = await this.confirmationModal.open("Remove Player", message, "Remove", true);

    if (confirmed) {
      try {
        const response = await apiService.withdrawPlayer(this.leagueId, playerId);
        if (response.success) {
          showNotification("Player removed successfully", "success");
          await this.render();
        } else {
          showNotification(response.error || "Failed to remove player", "error");
        }
      } catch (error) {
        console.error("Error withdrawing player:", error);
        showNotification("Error removing player", "error");
      }
    }
  }
}
